import apiClient from './client';
import type {
  JlrAuthStartResult,
  RpaCheck,
  ThirdPartyPlugin,
  ThirdPartyUploadTask,
  ThirdPartyUploadTaskList,
  ThirdPartyUploadTriggerResult,
} from '../types';

/**
 * 第三方插件目录（只读）。接入参数与凭据由插件内置，接口不返回任何配置字段。
 * @param availableOnly 仅返回可选用的插件，供项目表单下拉使用
 */
export const getThirdPartyPlugins = async (
  availableOnly?: boolean
): Promise<ThirdPartyPlugin[]> => {
  const { data } = await apiClient.get<ThirdPartyPlugin[]>('/third-party/plugins/', {
    params: availableOnly ? { available_only: true } : undefined,
  });
  return data;
};

export interface TriggerUploadOptions {
  /** 是否重传已成功的文件 */
  includeSucceeded?: boolean;
  /**
   * 第三方制品库目标目录（Feature 009）。仅插件 requires_target_dir 为真时必填，
   * 其他插件传了也会被后端忽略。
   */
  targetDir?: string;
  /** RPA 校验未通过后的用户确认（Feature 008） */
  confirm?: { check_id: number; decision: 'force' | 'cancel' };
  /**
   * 交互认证会话（JLR SharePoint）。由 `startJlrAuth` 返回，仅
   * `requires_interactive_auth` 的插件需要。
   */
  authSession?: string;
  /** 父目录链接（JLR SharePoint）。留空则用后端配置的默认值 */
  parentUrl?: string;
  /** 父目录下的文件夹，可多级（JLR SharePoint 必填） */
  folder?: string;
  /**
   * 用户已确认覆盖目标文件夹里的同名文件。
   * 默认不传：后端预检到重名会返回 409 `files_exist`，前端弹窗询问后带 true 重试。
   */
  overwrite?: boolean;
  /** 用户选择只上传不同名的文件（与 overwrite 互斥） */
  skipExisting?: boolean;
}

/** 目标文件夹里已存在的同名文件（409 files_exist 的附带信息） */
export interface ExistingFileConflict {
  name: string;
  label: string;
  local_size: number;
  remote_size: number;
  /** 两侧大小一致 / 不一致 / 任一侧未知（null） */
  same_size: boolean | null;
}

/** 409 files_exist 的完整载荷：冲突清单 + 本次一共要传几个文件 */
export interface ExistingFilesCheck {
  conflicts: ExistingFileConflict[];
  total: number;
  /** 后端的原始提示，冲突清单缺失时兜底展示 */
  message?: string;
}

/**
 * 启动 JLR SharePoint 的交互式认证。
 *
 * 该站点强制 MFA：后端会起一个无头浏览器填账号密码，阻塞到登录页出现
 * Authenticator 的号码匹配数字再返回。**密码只在这一请求里存在，后端不落库**。
 * 用户在手机上输入 `sign_number` 并批准后，带 `session_id` 调 `triggerUpload`。
 */
export const startJlrAuth = async (
  releaseId: number,
  payload: { username: string; password: string }
): Promise<JlrAuthStartResult> => {
  const { data } = await apiClient.post<JlrAuthStartResult>(
    `/third-party/releases/${releaseId}/auth/start/`,
    payload,
    // 后端要等无头浏览器走完账号→密码→号码页，实测 15~40 秒
    { timeout: 120000 }
  );
  return data;
};

/**
 * 触发产物包下全部文件的第三方上传。后端立即返回 202，实际推送在后台进行。
 * 重复触发是幂等的：已在队列中的文件会被跳过（created_count 为 0）。
 *
 * 后端先调 RPA check_valid：未通过时返回 409 `rpa_check_not_passed`，
 * 用 `getRpaCheckFromError` 取出 check 弹窗，确认后带 `confirm` 再次调用。
 * `decision: 'cancel'` 时后端返回 `{cancelled: true}`。
 */
export const triggerUpload = async (
  releaseId: number,
  opts: TriggerUploadOptions = {}
): Promise<ThirdPartyUploadTriggerResult> => {
  const body: Record<string, unknown> = { include_succeeded: opts.includeSucceeded ?? false };
  // 传了才带，且**空串也带**：德赛的可选目录允许显式清空（回到分享根目录）；
  // 完全不传（undefined）表示沿用发布记录上的已有值。
  if (opts.targetDir !== undefined) body.target_dir = opts.targetDir;
  // 交互认证型插件的三项：只在确实有值时带，避免给其他插件塞空字段
  if (opts.authSession) body.auth_session = opts.authSession;
  if (opts.parentUrl !== undefined) body.parent_url = opts.parentUrl;
  if (opts.folder !== undefined) body.folder = opts.folder;
  if (opts.overwrite) body.overwrite = true;
  if (opts.skipExisting) body.skip_existing = true;
  if (opts.confirm) body.confirm = opts.confirm;
  const { data } = await apiClient.post<ThirdPartyUploadTriggerResult>(
    `/third-party/releases/${releaseId}/upload/`,
    body
  );
  return data;
};

/** 从 triggerUpload 的异常中取出 409 附带的 RPA 校验信息；不是该错误时返回 null */
export const getRpaCheckFromError = (e: unknown): RpaCheck | null => {
  const resp = (e as { response?: { status?: number; data?: { error?: string; check?: RpaCheck } } })
    .response;
  if (resp?.status === 409 && resp.data?.error === 'rpa_check_not_passed' && resp.data.check) {
    return resp.data.check;
  }
  return null;
};

/** 从 triggerUpload 的异常中取出 409 附带的同名文件冲突清单；不是该错误时返回 null */
export const getExistingFilesFromError = (e: unknown): ExistingFilesCheck | null => {
  const resp = (e as {
    response?: {
      status?: number;
      data?: { error?: string; message?: string; conflicts?: ExistingFileConflict[]; total?: number };
    };
  }).response;
  const data = resp?.data;
  // 只认契约里的 error 码，不强求 conflicts 一定在：只要后端说是「同名文件」，
  // 就必须把覆盖/跳过/取消的选择权交给用户，不能因为少个字段退化成一条报错提示。
  if (resp?.status !== 409 || data?.error !== 'files_exist') return null;
  const conflicts = Array.isArray(data.conflicts) ? data.conflicts : [];
  return {
    conflicts,
    total: Number(data.total ?? conflicts.length),
    message: data.message,
  };
};

/** 查询产物包的逐文件上传状态与聚合状态，供详情页轮询 */
export const getUploadTasks = async (
  releaseId: number
): Promise<ThirdPartyUploadTaskList> => {
  const { data } = await apiClient.get<ThirdPartyUploadTaskList>(
    `/third-party/releases/${releaseId}/tasks/`
  );
  return data;
};

/** 重试单个失败的文件上传。仅 failed 状态可重试，其他状态后端返回 invalid_state */
export const retryTask = async (taskId: number): Promise<ThirdPartyUploadTask> => {
  const { data } = await apiClient.post<ThirdPartyUploadTask>(
    `/third-party/tasks/${taskId}/retry/`
  );
  return data;
};
